/**
 * Recursive renderer for the workspace tree. Renders PanelContainers
 * inside react-resizable-panels Groups (v4 API: Group + Panel + Separator).
 */
import { Group, Panel, Separator } from 'react-resizable-panels'
import { isPanelNode, type WorkspaceNode } from '@/lib/workspace/types'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { cn } from '@/lib/utils'
import { PanelContainer } from './PanelContainer'

interface WorkspaceGridProps {
  node: WorkspaceNode
}

export function WorkspaceGrid({ node }: WorkspaceGridProps) {
  const setSplitSizes = useWorkspaceStore((s) => s.setSplitSizes)
  const maximizedPanelId = useWorkspaceStore((s) => s.maximizedPanelId)

  // If a panel is maximized, render only that one full-bleed.
  if (maximizedPanelId) {
    const find = (n: WorkspaceNode): WorkspaceNode | null => {
      if (isPanelNode(n)) return n.id === maximizedPanelId ? n : null
      return find(n.children[0]) ?? find(n.children[1])
    }
    const target = find(node)
    if (target && isPanelNode(target)) return <PanelContainer panel={target} />
  }

  if (isPanelNode(node)) return <PanelContainer panel={node} />

  const orientation = node.direction === 'horizontal' ? 'horizontal' : 'vertical'

  return (
    <Group
      orientation={orientation}
      id={node.id}
      style={{ height: '100%', width: '100%' }}
      onLayoutChanged={(layout) => {
        // v4: layout is a map { [panelId]: number }. Reconstruct sizes by panel order.
        const a = layout[`${node.id}-a`]
        const b = layout[`${node.id}-b`]
        if (typeof a === 'number' && typeof b === 'number') setSplitSizes(node.id, [a, b])
      }}
    >
      <Panel id={`${node.id}-a`} defaultSize={node.sizes[0]} minSize={15}>
        <WorkspaceGrid node={node.children[0]} />
      </Panel>
      <Separator
        className={cn(
          'group bg-border hover:bg-primary/40 transition-colors',
          orientation === 'horizontal' ? 'w-1 cursor-col-resize' : 'h-1 cursor-row-resize',
        )}
      />
      <Panel id={`${node.id}-b`} defaultSize={node.sizes[1]} minSize={15}>
        <WorkspaceGrid node={node.children[1]} />
      </Panel>
    </Group>
  )
}
